import React, { useEffect, useRef, useState } from "react";
import { Chip, Collapse, IconButton, Paper, TextField, Tooltip } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import Clear from "@material-ui/icons/Clear";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { isArray, isString } from "lodash";
import toastError from "../../errors/toastError";
import api from "../../services/api";

export function TagsContainer({ ticket }) {
    const theme = useTheme();
    const [tags, setTags] = useState([]);
    const [selecteds, setSelecteds] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const isMounted = useRef(true);

    useEffect(() => {
        return () => { isMounted.current = false; };
    }, []);

    useEffect(() => {
        if (isMounted.current) {
            loadTags().then(() => {
                if (Array.isArray(ticket.tags)) {
                    setSelecteds(ticket.tags);
                    // auto-expand se já tem tags
                    if (ticket.tags.length > 0) setExpanded(true);
                } else {
                    setSelecteds([]);
                }
            });
        }
    }, [ticket]);

    const createTag = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    };

    const loadTags = async () => {
        try {
            const { data } = await api.get(`/tags/list`);
            if (isMounted.current) setTags(data);
        } catch (err) {
            toastError(err);
        }
    };

    const syncTags = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags/sync`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    };

    const onChange = async (value, reason) => {
        let optionsChanged = [];
        if (reason === "create-option") {
            if (isArray(value)) {
                for (let item of value) {
                    if (isString(item)) {
                        const newTag = await createTag({ name: item });
                        optionsChanged.push(newTag);
                    } else {
                        optionsChanged.push(item);
                    }
                }
            }
            await loadTags();
        } else {
            optionsChanged = value;
        }
        setSelecteds(optionsChanged);
        await syncTags({ ticketId: ticket.id, tags: optionsChanged });
    };

    const hasContent = selecteds.length > 0 || expanded;

    return (
        <Paper
            elevation={0}
            style={{
                borderBottom: hasContent ? "1px solid rgba(0,0,0,0.08)" : "none",
                display: "flex",
                alignItems: "flex-start",
                minHeight: hasContent ? undefined : 0,
            }}
        >
            <Tooltip title={expanded ? "Recolher tags" : "Gerenciar tags"} placement="right">
                <IconButton
                    size="small"
                    onClick={() => setExpanded((v) => !v)}
                    style={{
                        margin: "6px 4px 6px 8px",
                        color: selecteds.length > 0 ? "#2D7BE8" : theme.palette.text.disabled,
                        flexShrink: 0,
                    }}
                >
                    <LocalOfferIcon style={{ fontSize: 16 }} />
                    {selecteds.length > 0 && !expanded && (
                        <span style={{
                            position: "absolute",
                            top: 0, right: 0,
                            width: 8, height: 8,
                            borderRadius: "50%",
                            backgroundColor: "#2D7BE8",
                        }} />
                    )}
                </IconButton>
            </Tooltip>
            {selecteds.length > 0 && !expanded && (
                <Tooltip title="Remover todas as tags">
                    <IconButton
                        size="small"
                        onClick={async () => {
                            setSelecteds([]);
                            await syncTags({ ticketId: ticket.id, tags: [] });
                        }}
                        style={{ margin: "6px 8px 6px 0", color: theme.palette.text.secondary, flexShrink: 0 }}
                    >
                        <Clear style={{ fontSize: 14 }} />
                    </IconButton>
                </Tooltip>
            )}

            <Collapse in={expanded} style={{ flex: 1 }}>
                <div style={{ padding: "4px 8px 4px 0" }}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={tags}
                        value={selecteds}
                        freeSolo
                        onChange={(e, v, r) => onChange(v, r)}
                        getOptionLabel={(option) => option.name}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    key={index}
                                    style={{
                                        background: option.color || "#999",
                                        color: "#fff",
                                        marginRight: 4,
                                        fontWeight: 600,
                                        borderRadius: 12,
                                        fontSize: "0.7em",
                                        height: 22,
                                        border: "none",
                                        letterSpacing: "0.03em",
                                    }}
                                    label={option.name}
                                    {...getTagProps({ index })}
                                    size="small"
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                placeholder="Adicionar tag..."
                                InputProps={{ ...params.InputProps, disableUnderline: true }}
                                inputProps={{ ...params.inputProps, style: { fontSize: "0.85rem" } }}
                            />
                        )}
                        PaperComponent={({ children }) => (
                            <Paper elevation={3} style={{ width: 380 }}>
                                {children}
                            </Paper>
                        )}
                    />
                </div>
            </Collapse>
        </Paper>
    );
}
